import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t, i18n } = useTranslation();

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const handleLanguageClick = (event) => setAnchorEl(event.currentTarget);
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setAnchorEl(null);
  };

  // ✅ Dynamically get all languages from i18n
  const availableLanguages = i18n.options.resources
    ? Object.keys(i18n.options.resources)
    : ["en", "hi", "gu"];

  // ✅ Dynamic labels (optional, from JSON or custom mapping)
  const languageLabels = {
    en: "English",
    hi: "हिंदी",
    gu: "ગુજરાતી",
  };

  const navLinks = [
    { title: t("header.home"), path: "/" },
    { title: t("header.biography"), path: "/biography" },
    { title: t("header.gallery"), path: "/gallery" },
    { title: t("header.ebook"), path: "/ebook" },
    { title: t("header.slogans"), path: "/slogans" },
    { title: t("header.functions"), path: "/functions" },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, md: 4 },
          }}
        >
          {/* Left section: Logo + Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src="/images/2.png"
              alt="Gurudev Avatar"
              sx={{ width: 48, height: 48 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.2rem" },
                color: "#FFD700",
              }}
            >
              {t("header.title")}
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 3,
            }}
          >
            {navLinks.map((link) => (
              <Button
                key={link.path}
                component={Link}
                to={link.path}
                sx={{
                  color: "#FFD700",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: { xs: "1rem", md: "1rem" }, // 👈 increased font size (responsive)
                  "&:hover": { color: "#fff" },
                }}
              >
                {link.title}
              </Button>
            ))}

            {/* 🌐 Dynamic Language Dropdown */}
            <Button
              onClick={handleLanguageClick}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                color: "#FFD700",
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "1rem", md: "1rem" }, // 👈 increased font size (responsive)
                "&:hover": { color: "#fff" },
              }}
            >
              {languageLabels[i18n.language] || i18n.language.toUpperCase()}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  backgroundColor: "#111",
                  color: "#FFD700",
                  border: "1px solid #333",
                },
              }}
            >
              {availableLanguages.map((lang) => (
                <MenuItem key={lang} onClick={() => handleLanguageChange(lang)}>
                  {languageLabels[lang] || lang.toUpperCase()}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            sx={{ display: { xs: "flex", md: "none" }, color: "#fff" }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { width: 250, backgroundColor: "#111", color: "#fff" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid #333",
          }}
        >
          <Typography variant="h6">Menu</Typography>
          <IconButton onClick={toggleDrawer(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List>
          {navLinks.map((link) => (
            <ListItem key={link.path} disablePadding>
              <ListItemButton
                component={Link}
                to={link.path}
                onClick={toggleDrawer(false)}
              >
                <ListItemText primary={link.title} />
              </ListItemButton>
            </ListItem>
          ))}

          {/* 🌐 Language Switch in Drawer */}
          <ListItem>
            <ListItemButton onClick={handleLanguageClick}>
              <ListItemText
                primary={
                  languageLabels[i18n.language] || i18n.language.toUpperCase()
                }
              />
              <ArrowDropDownIcon />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;